// Calculation utilities for TradeCopilot

export const calculateMarketQualityScore = (checklist, analysis) => {
  let score = 0;
  const weights = {
    marketTrending: 20,
    clearDirection: 20,
    volatilitySufficient: 15,
    noMajorNews: 20,
    liquidityHigh: 25
  };
  
  Object.entries(checklist).forEach(([key, value]) => {
    if (value === true) {
      score += weights[key] || 0;
    }
  });
  
  return Math.min(100, Math.max(0, score));
};

export const getMarketQualityMessage = (score) => {
  if (score >= 0 && score < 40) {
    return {
      message: 'Avoid trading',
      color: 'text-destructive',
      bgColor: 'bg-destructive/10',
      borderColor: 'border-destructive/50'
    };
  } else if (score >= 40 && score < 70) {
    return {
      message: 'Cautious trading',
      color: 'text-warning',
      bgColor: 'bg-warning/10',
      borderColor: 'border-warning/50'
    };
  } else {
    return {
      message: 'Good conditions',
      color: 'text-success',
      bgColor: 'bg-success/10',
      borderColor: 'border-success/50'
    };
  }
};

export const calculateRiskReward = (entryPrice, stopLoss, takeProfit) => {
  const entry = parseFloat(entryPrice);
  const sl = parseFloat(stopLoss);
  const tp = parseFloat(takeProfit);
  
  if (isNaN(entry) || isNaN(sl) || isNaN(tp) || entry === 0) {
    return null;
  }
  
  const riskAmount = Math.abs(entry - sl);
  const rewardAmount = Math.abs(tp - entry);
  
  const riskPercent = (riskAmount / entry) * 100;
  const rewardPercent = (rewardAmount / entry) * 100;
  const riskRewardRatio = riskAmount > 0 ? rewardAmount / riskAmount : 0;
  
  return {
    riskPercent: riskPercent.toFixed(2),
    rewardPercent: rewardPercent.toFixed(2),
    riskRewardRatio: riskRewardRatio.toFixed(2)
  };
};

export const checkTradingPlanViolations = (formData, checklist, rrData, tradingPlan, todaysTrades = []) => {
  if (!tradingPlan || !tradingPlan.enabled) {
    return { hasViolations: false, violations: [] };
  }
  
  const violations = [];
  
  // Check max trades per day
  if (tradingPlan.maxTradesPerDay && tradingPlan.maxTradesPerDay > 0) {
    const today = new Date().toDateString();
    const tradesCountToday = todaysTrades.filter(t => 
      new Date(t.timestamp).toDateString() === today
    ).length;
    
    if (tradesCountToday >= tradingPlan.maxTradesPerDay) {
      violations.push(`Max trades per day limit reached (${tradingPlan.maxTradesPerDay})`);
    }
  }
  
  // Check minimum R:R ratio
  if (tradingPlan.minRiskReward && rrData) {
    const rr = parseFloat(rrData.riskRewardRatio);
    if (rr < tradingPlan.minRiskReward) {
      violations.push(`R:R ratio ${rr} is below minimum ${tradingPlan.minRiskReward}`);
    }
  }
  
  // Check max risk per trade
  if (tradingPlan.maxRiskPerTrade && rrData) {
    const risk = parseFloat(rrData.riskPercent);
    if (risk > tradingPlan.maxRiskPerTrade) {
      violations.push(`Risk ${risk}% exceeds maximum ${tradingPlan.maxRiskPerTrade}%`);
    }
  }
  
  // Check allowed setup types
  if (tradingPlan.allowedSetupTypes && tradingPlan.allowedSetupTypes.length > 0) {
    if (formData.setupType && !tradingPlan.allowedSetupTypes.includes(formData.setupType)) {
      violations.push(`Setup type "${formData.setupType}" is not in your allowed list`);
    }
  }
  
  // Check trading session
  if (tradingPlan.tradingSessions && tradingPlan.tradingSessions.length > 0) {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentTime = currentHour * 60 + currentMinute;
    
    const isInSession = tradingPlan.tradingSessions.some(session => {
      const [startHour, startMin] = session.start.split(':').map(Number);
      const [endHour, endMin] = session.end.split(':').map(Number);
      const startTime = startHour * 60 + startMin;
      const endTime = endHour * 60 + endMin;
      return currentTime >= startTime && currentTime <= endTime;
    });
    
    if (!isInSession) {
      violations.push('Current time is outside your allowed trading sessions');
    }
  }
  
  // Check daily loss limit
  if (tradingPlan.dailyLossLimit && tradingPlan.dailyLossLimit > 0) {
    const today = new Date().toDateString();
    const todaysLosses = todaysTrades.filter(t => 
      new Date(t.timestamp).toDateString() === today && t.result === 'loss'
    );
    
    const totalLossPercent = todaysLosses.reduce((sum, t) => {
      return sum + (parseFloat(t.riskPercent) || 0);
    }, 0);
    
    if (totalLossPercent >= tradingPlan.dailyLossLimit) {
      violations.push(`Daily loss limit ${tradingPlan.dailyLossLimit}% reached`);
    }
  }
  
  return {
    hasViolations: violations.length > 0,
    violations
  };
};

export const validateTradeSetup = (formData, checklist, rrData) => {
  const issues = [];
  const mechanicalViolations = [];
  
  // Check R:R ratio (minimum 2R for mechanical model)
  if (rrData && parseFloat(rrData.riskRewardRatio) < 2.0) {
    issues.push('Risk to reward ratio is less than 2.0 (mechanical model requires 2R minimum)');
    mechanicalViolations.push('R:R < 2.0');
  }
  
  // Check Market Structure criteria
  if (!checklist.clearTrendExists) {
    mechanicalViolations.push('No clear trend confirmed');
  }
  if (!checklist.notInSidewaysRange) {
    mechanicalViolations.push('Price inside sideways range');
  }
  
  // Check Pullback Quality criteria
  if (!checklist.pullbackMultipleCandles) {
    mechanicalViolations.push('Pullback does not show multiple small candles/consolidation');
  }
  if (!checklist.noLargeReversalCandle) {
    mechanicalViolations.push('Large reversal candle present');
  }
  
  // Check Entry Trigger
  if (!checklist.entryOnBreakOrClose) {
    mechanicalViolations.push('Entry not on break of pullback or continuation candle close');
  }
  
  // Check Risk Placement
  if (!checklist.stopBeyondStructure) {
    mechanicalViolations.push('Stop not placed beyond pullback structure');
  }
  if (!checklist.takeProfitMin2R) {
    mechanicalViolations.push('Take profit does not allow minimum 2R');
  }
  
  // Combine all violations
  const allIssues = [...mechanicalViolations, ...issues];
  
  return {
    isValid: allIssues.length === 0,
    issues: allIssues,
    isMechanicalViolation: mechanicalViolations.length > 0
  };
};

export const calculateTradeScore = (checklist, rrData, emotion) => {
  let score = 50; // Base score
  
  const weights = {
    strongMomentum: 10,
    pullbackPresent: 8,
    enteringNearStructure: 8,
    riskDefined: 12,
    notChasingPrice: 10,
    notEmotional: 12
  };
  
  // Add points for positive checks
  Object.entries(checklist).forEach(([key, value]) => {
    if (value === true && weights[key]) {
      score += weights[key];
    }
  });
  
  // Subtract for negative checks
  if (checklist.chasingPrice) {
    score -= 15;
  }
  
  // R:R bonus
  if (rrData) {
    const rr = parseFloat(rrData.riskRewardRatio);
    if (rr >= 2.0) score += 15;
    else if (rr >= 1.5) score += 10;
    else score -= 10;
  }
  
  // Emotion penalty
  if (emotion === 'FOMO' || emotion === 'revenge') {
    score -= 20;
  } else if (emotion === 'bored') {
    score -= 10;
  }
  
  return Math.min(100, Math.max(0, score));
};

export const calculateJournalStats = (trades) => {
  if (!trades || trades.length === 0) {
    return {
      totalTrades: 0,
      winRate: 0,
      avgRR: 0,
      mostCommonMistake: 'N/A',
      performanceByEmotion: {},
      ruleFollowingStats: { totalTrades: 0, winRate: 0 },
      ruleBreakingStats: { totalTrades: 0, winRate: 0 },
      plannedTradesStats: { totalTrades: 0, winRate: 0 },
      unplannedTradesStats: { totalTrades: 0, winRate: 0 }
    };
  }
  
  const wins = trades.filter(t => t.result === 'win').length;
  const winRate = (wins / trades.length) * 100;
  
  const rrValues = trades
    .filter(t => t.riskRewardRatio)
    .map(t => parseFloat(t.riskRewardRatio));
  const avgRR = rrValues.length > 0 
    ? rrValues.reduce((a, b) => a + b, 0) / rrValues.length 
    : 0;
  
  const mistakes = {};
  trades.forEach(t => {
    if (t.mistake) {
      mistakes[t.mistake] = (mistakes[t.mistake] || 0) + 1;
    }
  });
  const mostCommonMistake = Object.keys(mistakes).length > 0
    ? Object.entries(mistakes).sort((a, b) => b[1] - a[1])[0][0]
    : 'None recorded';
  
  const emotionStats = {};
  trades.forEach(t => {
    if (t.emotion) {
      if (!emotionStats[t.emotion]) {
        emotionStats[t.emotion] = { total: 0, wins: 0 };
      }
      emotionStats[t.emotion].total++;
      if (t.result === 'win') {
        emotionStats[t.emotion].wins++;
      }
    }
  });
  
  const performanceByEmotion = {};
  Object.entries(emotionStats).forEach(([emotion, stats]) => {
    performanceByEmotion[emotion] = {
      winRate: (stats.wins / stats.total) * 100,
      count: stats.total
    };
  });
  
  // Calculate rule-following vs rule-breaking stats
  const ruleFollowingTrades = trades.filter(t => !t.ruleViolation);
  const ruleBreakingTrades = trades.filter(t => t.ruleViolation);
  
  const ruleFollowingWins = ruleFollowingTrades.filter(t => t.result === 'win').length;
  const ruleBreakingWins = ruleBreakingTrades.filter(t => t.result === 'win').length;
  
  const ruleFollowingStats = {
    totalTrades: ruleFollowingTrades.length,
    winRate: ruleFollowingTrades.length > 0 
      ? (ruleFollowingWins / ruleFollowingTrades.length) * 100 
      : 0
  };
  
  const ruleBreakingStats = {
    totalTrades: ruleBreakingTrades.length,
    winRate: ruleBreakingTrades.length > 0 
      ? (ruleBreakingWins / ruleBreakingTrades.length) * 100 
      : 0
  };
  
  // Calculate planned vs unplanned trades stats
  const plannedTrades = trades.filter(t => t.isPlanned && !t.isUnplanned);
  const unplannedTrades = trades.filter(t => t.isUnplanned);
  
  const plannedWins = plannedTrades.filter(t => t.result === 'win').length;
  const unplannedWins = unplannedTrades.filter(t => t.result === 'win').length;
  
  const plannedTradesStats = {
    totalTrades: plannedTrades.length,
    winRate: plannedTrades.length > 0 
      ? (plannedWins / plannedTrades.length) * 100 
      : 0
  };
  
  const unplannedTradesStats = {
    totalTrades: unplannedTrades.length,
    winRate: unplannedTrades.length > 0 
      ? (unplannedWins / unplannedTrades.length) * 100 
      : 0
  };
  
  return {
    totalTrades: trades.length,
    winRate: winRate.toFixed(1),
    avgRR: avgRR.toFixed(2),
    mostCommonMistake,
    performanceByEmotion,
    ruleFollowingStats,
    ruleBreakingStats,
    plannedTradesStats,
    unplannedTradesStats
  };
};

export const getWeeklyInsights = (trades) => {
  if (!trades || trades.length === 0) return [];
  
  const insights = [];
  const stats = calculateJournalStats(trades);
  
  // Planned vs Unplanned insights
  if (stats.unplannedTradesStats.totalTrades > 0 && stats.plannedTradesStats.totalTrades > 0) {
    const diff = stats.plannedTradesStats.winRate - stats.unplannedTradesStats.winRate;
    if (diff > 15) {
      insights.push({
        type: 'error',
        message: `🚨 Planned trades perform ${diff.toFixed(1)}% better than unplanned trades (${stats.plannedTradesStats.winRate.toFixed(1)}% vs ${stats.unplannedTradesStats.winRate.toFixed(1)}%). Stop hindsight logging! Validate trades BEFORE execution.`
      });
    } else if (diff > 5) {
      insights.push({
        type: 'warning',
        message: `Pre-validated trades have ${diff.toFixed(1)}% higher win rate. Always use the validator before entering trades.`
      });
    }
  }
  
  if (stats.unplannedTradesStats.totalTrades > stats.plannedTradesStats.totalTrades) {
    insights.push({
      type: 'error',
      message: `${stats.unplannedTradesStats.totalTrades} unplanned trades vs ${stats.plannedTradesStats.totalTrades} planned. You're trading on impulse! Use the Trade Validator before every trade.`
    });
  }
  
  // Rule violation insights
  if (stats.ruleBreakingStats.totalTrades > 0 && stats.ruleFollowingStats.totalTrades > 0) {
    const diff = stats.ruleFollowingStats.winRate - stats.ruleBreakingStats.winRate;
    if (diff > 10) {
      insights.push({
        type: 'warning',
        message: `Rule-following trades have ${diff.toFixed(1)}% higher win rate (${stats.ruleFollowingStats.winRate.toFixed(1)}% vs ${stats.ruleBreakingStats.winRate.toFixed(1)}%). Stick to your trading plan!`
      });
    } else if (diff < -10) {
      insights.push({
        type: 'info',
        message: `Rule-breaking trades performing better. Consider reviewing and adjusting your trading plan rules.`
      });
    }
  }
  
  // Emotion-based insights
  Object.entries(stats.performanceByEmotion).forEach(([emotion, data]) => {
    if (data.winRate < 40 && data.count >= 3) {
      insights.push({
        type: 'warning',
        message: `Trades taken during ${emotion.toUpperCase()} have ${data.winRate.toFixed(1)}% win rate (${data.count} trades). Consider avoiding trades in this emotional state.`
      });
    }
  });
  
  // Win rate insights
  if (parseFloat(stats.winRate) < 40) {
    insights.push({
      type: 'error',
      message: `Overall win rate is ${stats.winRate}%. Review your strategy and ensure strict adherence to your trading plan.`
    });
  } else if (parseFloat(stats.winRate) >= 60) {
    insights.push({
      type: 'success',
      message: `Strong performance with ${stats.winRate}% win rate. Maintain discipline and continue following your process.`
    });
  }
  
  // R:R insights
  if (parseFloat(stats.avgRR) < 1.5) {
    insights.push({
      type: 'warning',
      message: `Average R:R of ${stats.avgRR} is below target. Focus on better risk-reward setups (target 1.5 or higher).`
    });
  }
  
  // Mistake pattern
  if (stats.mostCommonMistake && stats.mostCommonMistake !== 'None recorded') {
    insights.push({
      type: 'info',
      message: `Most common mistake: "${stats.mostCommonMistake}". Create a pre-trade checklist to address this pattern.`
    });
  }
  
  return insights;
};